import { useQuery } from '@apollo/client';
import { CircularProgress, List, ListItem, Typography } from '@mui/material';
import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { useLanguageCode } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { Trans } from 'react-i18next';

import { useAuthRoles } from '~/hooks/useAuthRoles/useAuthRoles';
import { CMDI_NEEDED_ROLES, useCmdiHasuraContext } from '~/hooks/useCmdiHasuraContext/useCmdiHasuraContext';
import { RouteIdPart } from '~/routes/RouteId';

import { CMDI_META_QUERY } from './CmdiMetaQuery.gql';
import { CustomDataNavLinksItem } from './CustomDataNavListItem';

export const CustomDataNavList = () => {
  const { hasAnyRole } = useAuthRoles();

  const { hasPermission } = usePermissions();
  const hasNeededRole = hasAnyRole(CMDI_NEEDED_ROLES);
  const context = useCmdiHasuraContext();

  const { data, loading } = useQuery(CMDI_META_QUERY, {
    context,
    skip: !hasNeededRole,
  });

  const entities = data?.cmdi.meta_entity;

  const entitiesFiltered = useMemo(
    () =>
      entities?.filter((item) => {
        if (item.id === (RouteIdPart.savings as string)) {
          return false;
        }

        return item.needsPermission ? hasPermission(item.needsPermission as Permission) : true;
      }),
    [entities, hasPermission],
  );

  let errorContent: string | null = null;

  if (!hasNeededRole) {
    errorContent = 'Cmdi.error.role';
  } else if (entitiesFiltered && entitiesFiltered.length === 0) {
    errorContent = 'Cmdi.error.empty';
  }

  const { languageCode } = useLanguageCode();
  const languageCodeUpper = languageCode.toUpperCase();

  return (
    <List>
      {loading ? (
        <ListItem>
          <Typography variant="body2">
            <CircularProgress size={14} sx={{ marginRight: 2 }} />
            <Trans i18nKey="Nav.dataFoundationLoading" />
          </Typography>
        </ListItem>
      ) : errorContent ? (
        <ListItem sx={{ whiteSpace: 'normal' }}>
          <Typography variant="body2">
            <Trans i18nKey={errorContent} />
          </Typography>
        </ListItem>
      ) : (
        entitiesFiltered?.map((item) => (
          <CustomDataNavLinksItem key={item.id} id={item.id} title={item.i18n[languageCodeUpper]} />
        ))
      )}
    </List>
  );
};
